import React from 'react';
import { ArrowLeft } from 'lucide-react';

interface RiskDetailsHeaderProps {
  mode: 'create' | 'edit' | 'view';
  /** Display id used to title the page in edit mode (rendered as "Risk {id}"). */
  subtitle: string;
  /** Edit-mode descriptive line below the title (typically the risk description). */
  description?: string;
  onBack: () => void;
}

const CREATE_TITLE = 'Create Risk';
const CREATE_SUBTITLE = 'Create a new risk record';
const EDIT_SUBTITLE_FALLBACK = 'Update narrative, type, and associations. System id does not change.';
const DESCRIPTION_TRUNCATE_LIMIT = 50;

function truncateText(value: string, limit: number): string {
  if (value.length <= limit) return value;
  return `${value.slice(0, limit)}...`;
}

export const RiskDetailsHeader = ({
  mode,
  subtitle,
  description,
  onBack,
}: RiskDetailsHeaderProps) => {
  const editTitle = subtitle ? `Risk ${subtitle}` : 'Edit Risk';
  const editDescription = (description ?? '').trim() || EDIT_SUBTITLE_FALLBACK;
  const displayDescription = truncateText(editDescription, DESCRIPTION_TRUNCATE_LIMIT);

  return (
    <div className="bg-white border-b border-gray-200 px-8 py-4 flex items-center justify-between sticky top-0 z-10">
      <div className="flex items-center gap-4 min-w-0">
        <button
          type="button"
          onClick={onBack}
          className="p-2 hover:bg-gray-100 rounded-full transition-colors"
          aria-label="Back to risks"
        >
          <ArrowLeft className="h-5 w-5 text-gray-500" />
        </button>
        <div className="min-w-0">
          {mode !== 'create' ? (
            <>
              <div className="flex items-center gap-3">
                <h1 className="text-xl font-bold text-gray-900 truncate">{editTitle}</h1>
              </div>
              <p
                className="text-sm text-gray-500 mt-0.5 truncate"
                title={displayDescription !== editDescription ? editDescription : undefined}
              >
                {displayDescription}
              </p>
            </>
          ) : (
            <>
              <h1 className="text-xl font-semibold text-gray-900 truncate">{CREATE_TITLE}</h1>
              <p className="text-sm text-gray-500 truncate">{CREATE_SUBTITLE}</p>
            </>
          )}
        </div>
      </div>
      <button
        type="button"
        onClick={onBack}
        className="px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-md hover:bg-gray-50 transition-colors"
      >
        Cancel
      </button>
    </div>
  );
};

export default RiskDetailsHeader;
